import { useState, useEffect, useCallback } from 'react';
import { useChat } from '../../context/ChatContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  BarChart, Bar, 
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell 
} from 'recharts';
import { 
  Store,
  TrendingUp,
  Download,
  Brain,
  MapPin,
  DollarSign,
  Users,
  ShoppingCart,
  ArrowUpRight,
  ArrowDownRight,
  ExternalLink,
  Zap,
  ZoomIn
} from 'lucide-react';
import axios from 'axios';
import { FormulaTooltip } from '../FormulaTooltip';
import DrillDownModal from '../DrillDownModal';
import ChartZoomModal, { ChartZoomButton } from '../ChartZoomModal';
import DeepAIInsights from '../DeepAIInsights';

import { API_BASE_URL as API } from '../../utils/apiConfig';
const COLORS = ['#8B5CF6', '#EC4899', '#14B8A6', '#EAB308', '#3B82F6', '#F97316', '#10B981', '#6366F1'];

export default function StoreDashboard() {
  const { selectedBrand } = useChat();
  const [storeData, setStoreData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStore, setSelectedStore] = useState(null);
  const [drillDown, setDrillDown] = useState({ open: false, type: null, data: null, title: '' });
  const [isLiveData, setIsLiveData] = useState(false);
  const [chartZoom, setChartZoom] = useState({ open: false, type: '', data: [], title: '', config: {} });

  // Chart zoom handlers
  const openChartZoom = (chartType, chartData, chartTitle, chartConfig = {}) => {
    setChartZoom({ open: true, type: chartType, data: chartData, title: chartTitle, config: chartConfig });
  };
  const closeChartZoom = () => setChartZoom({ open: false, type: '', data: [], title: '', config: {} });

  // Check for live data
  const getDbBrandId = useCallback(() => {
    return selectedBrand?.db_brand_id || selectedBrand?.settings?.db_brand_id || null;
  }, [selectedBrand]);

  useEffect(() => {
    if (selectedBrand) {
      fetchStoreData();
    }
  }, [selectedBrand]);

  const openDrillDown = (storeCode, storeName) => {
    setDrillDown({ 
      open: true, 
      type: 'store', 
      data: { value: storeCode, context: { name: storeName } }, 
      title: `Store Details: ${storeName}` 
    });
  };

  const closeDrillDown = () => {
    setDrillDown({ open: false, type: null, data: null, title: '' });
  };

  const fetchStoreData = async () => {
    if (!selectedBrand) return;
    
    setLoading(true);
    
    // Check for live Data Bridge - mark as live if brand has db_brand_id
    const dbBrandId = getDbBrandId();
    
    // Try the store-performance API first (uses local MongoDB)
    try {
      const response = await axios.post(
        `${API}/api/analytics/store-performance`,
        { brand_id: selectedBrand.brand_id, filters: {} },
        { withCredentials: true }
      );
      
      if (response.data.success && response.data.data?.stores?.length > 0) {
        // Transform store-performance data to match expected format
        const stores = response.data.data.stores.map(s => ({
          store_code: s.store_code || s.code || '',
          store_name: s.store_name || s.name || '',
          city: s.city || '',
          revenue: s.revenue || 0,
          customers: s.customers || 0,
          atv: s.atv || 0,
          repeat_pct: s.repeat_pct || 0,
          health_score: s.health_score || 0
        }));
        setStoreData(stores);
        setIsLiveData(response.data.data?.source === 'live_data_bridge' || !!dbBrandId);
      } else {
        // Fallback to export API
        const exportResponse = await axios.post(
          `${API}/api/export/analytics`,
          { analysis_type: 'store_analysis', brand_id: selectedBrand.brand_id },
          { withCredentials: true }
        );
        
        if (exportResponse.data.csv_content) {
          setStoreData(parseCSV(exportResponse.data.csv_content));
        } else {
          setStoreData([]);
        }
      }
    } catch (err) {
      console.error('Store data error:', err);
      setStoreData([]);
    } finally {
      setLoading(false);
    }
  };

  // Proper CSV parser that handles quoted fields with commas
  const parseCSV = (csv) => {
    const lines = csv.trim().split('\n');
    if (lines.length < 2) return [];
    
    const parseCSVLine = (line) => {
      const result = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        
        if (char === '"') {
          if (inQuotes && line[i + 1] === '"') {
            current += '"';
            i++; // Skip the escaped quote
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    };
    
    const headers = parseCSVLine(lines[0]);
    const rows = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      const row = {};
      headers.forEach((header, idx) => {
        const val = values[idx];
        row[header] = val && !isNaN(val) ? parseFloat(val) : val;
      });
      rows.push(row);
    }
    return rows;
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(
        `${API}/api/export/download/store_analysis?brand_id=${selectedBrand.brand_id}`,
        { withCredentials: true, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `store_analysis_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-4">
          <Brain className="w-12 h-12 text-primary animate-pulse" />
          <p className="text-muted-foreground">Loading store analytics...</p>
        </div>
      </div>
    );
  }

  const topStores = storeData?.slice(0, 5) || [];
  const totalRevenue = storeData?.reduce((sum, s) => sum + (s.revenue || s.Revenue || 0), 0) || 0;
  const totalCustomers = storeData?.reduce((sum, s) => sum + (s.customers || s.Customers || 0), 0) || 0;
  const avgATV = storeData?.length ? storeData.reduce((sum, s) => sum + (s.atv || s.ATV || 0), 0) / storeData.length : 0;

  return (
    <div className="space-y-6" data-testid="store-dashboard">
      {/* Drill Down Modal */}
      <DrillDownModal 
        isOpen={drillDown.open}
        onClose={closeDrillDown}
        drillType={drillDown.type}
        drillData={drillDown.data}
        title={drillDown.title}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Store Performance</h2>
          <p className="text-muted-foreground">{storeData?.length || 0} stores analyzed</p>
        </div>
        <Button variant="outline" onClick={handleExport} className="gap-2">
          <Download className="w-4 h-4" />
          Export All Stores
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="kpi-total-stores">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <Store className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <FormulaTooltip metric="total_stores">
                  <p className="text-sm text-muted-foreground">Total Stores</p>
                </FormulaTooltip>
                <p className="text-2xl font-bold">{storeData?.length || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-total-revenue">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <FormulaTooltip metric="total_revenue">
                  <p className="text-sm text-muted-foreground">Total Revenue</p>
                </FormulaTooltip>
                <p className="text-2xl font-bold">₹{(totalRevenue / 10000000).toFixed(1)}Cr</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-total-customers">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <FormulaTooltip metric="total_customers">
                  <p className="text-sm text-muted-foreground">Total Customers</p>
                </FormulaTooltip>
                <p className="text-2xl font-bold">{totalCustomers.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-avg-atv">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <FormulaTooltip metric="avg_atv">
                  <p className="text-sm text-muted-foreground">Avg ATV</p>
                </FormulaTooltip>
                <p className="text-2xl font-bold">₹{Math.round(avgATV).toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top Stores by Revenue */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Top 10 Stores by Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={storeData?.slice(0, 10)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" 
                       tickFormatter={(v) => `₹${((v || 0)/10000000).toFixed(0)}Cr`} />
                <YAxis type="category" dataKey="store_name" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" width={150} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                  formatter={(value) => [`₹${((value || 0)/10000000).toFixed(2)}Cr`, 'Revenue']}
                />
                <Bar dataKey="revenue" fill="#8B5CF6" radius={[0, 4, 4, 0]}>
                  {storeData?.slice(0, 10).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top Stores by ATV */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Top 10 Stores by ATV</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={[...storeData || []].sort((a, b) => (b.atv || 0) - (a.atv || 0)).slice(0, 10)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" 
                       tickFormatter={(v) => `₹${(v || 0).toLocaleString()}`} />
                <YAxis type="category" dataKey="store_name" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" width={150} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                  formatter={(value) => [`₹${(value || 0).toLocaleString()}`, 'ATV']}
                />
                <Bar dataKey="atv" fill="#14B8A6" radius={[0, 4, 4, 0]}>
                  {[...storeData || []].sort((a, b) => (b.atv || 0) - (a.atv || 0)).slice(0, 10).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[(index + 2) % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Store Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">All Stores Performance</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-card">
                <tr className="border-b border-border bg-muted/30">
                  <th className="px-4 py-3 text-left text-sm font-semibold">Store</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold">Revenue</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold">Customers</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold">ATV</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold">Health</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Performance</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {storeData?.map((store, idx) => {
                  const isTop = idx < 5;
                  const isBottom = idx >= storeData.length - 5;
                  const storeName = store.store_name || store['Store Name'] || '';
                  const storeCode = store.store_code || store['Store Code'] || '';
                  const revenue = store.revenue || store.Revenue || 0;
                  const customers = store.customers || store.Customers || 0;
                  const atv = store.atv || store.ATV || 0;
                  const healthScore = store.health_score || 0;
                  return (
                    <tr 
                      key={idx} 
                      className={`border-b border-border/50 hover:bg-primary/5 cursor-pointer transition-colors ${
                        selectedStore === idx ? 'bg-primary/10' : ''
                      }`}
                      onClick={() => openDrillDown(storeCode, storeName)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                            <MapPin className="w-4 h-4 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="font-medium text-sm truncate max-w-[200px]">{storeName}</p>
                            <p className="text-xs text-muted-foreground">{store.city || storeCode}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-medium">₹{(revenue / 100000).toFixed(1)}L</td>
                      <td className="px-4 py-3 text-right">{customers?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right">₹{Math.round(atv)?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right">{healthScore}/100</td>
                      <td className="px-4 py-3 text-center">
                        {isTop ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/10 text-green-500 text-xs font-medium">
                            <ArrowUpRight className="w-3 h-3" />
                            Top
                          </span>
                        ) : isBottom ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/10 text-red-500 text-xs font-medium">
                            <ArrowDownRight className="w-3 h-3" />
                            Low
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full bg-muted text-muted-foreground text-xs font-medium">
                            Avg
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="gap-1 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDrillDown(storeCode, storeName);
                          }}
                        >
                          <ExternalLink className="w-3 h-3" />
                          View
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Deep AI Insights */}
      <DeepAIInsights
        brandId={selectedBrand?.brand_id}
        brandName={selectedBrand?.name}
        dashboardType="store"
        dashboardData={storeData}
        kpis={{
          total_customers: { value: storeData?.totals?.total_customers || 0 },
          total_revenue: { value: storeData?.totals?.total_revenue || 0 }
        }}
        stores={storeData?.stores || []}
      />

      {/* Drill Down Modal */}
      <DrillDownModal
        open={drillDown.open}
        onClose={closeDrillDown}
        drillType={drillDown.type}
        drillData={drillDown.data}
        title={drillDown.title}
        brandId={selectedBrand?.brand_id}
      />

      {/* Chart Zoom Modal */}
      <ChartZoomModal
        open={chartZoom.open}
        onClose={closeChartZoom}
        chartType={chartZoom.type}
        chartData={chartZoom.data}
        chartTitle={chartZoom.title}
        chartConfig={chartZoom.config}
        brandId={selectedBrand?.brand_id}
        dashboardType="store"
      />
    </div>
  );
}
